import type { ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import PageLoader from "@/components/base/Loading";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/lib/supabase";

interface ProtectedRouteProps {
  children: ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useCurrentUser();
  const { profile, loading: profileLoading } = useProfile();
  const location = useLocation();

  if (loading || (user && profileLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50">
        <PageLoader label="正在校验登录状态" />
      </div>
    );
  }

  if (!user) {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  if (profile?.status === "suspended") {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <AppHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-100 p-8 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-user-forbid-line text-2xl" />
            </span>
            <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-950">账号已被停用</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              你的账号目前处于停用状态，暂时无法访问受保护的数据。如有疑问，请联系平台客服协助处理。
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/"
                className="w-full rounded-md bg-primary-500 px-5 py-2.5 text-center text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
              >
                返回首页
              </Link>
              <button
                type="button"
                onClick={() => {
                  void supabase.auth.signOut();
                }}
                className="w-full cursor-pointer rounded-md border border-background-300 px-5 py-2.5 text-center text-sm font-medium text-foreground-800 transition-colors hover:bg-background-50 sm:w-auto"
              >
                退出登录
              </button>
            </div>
          </div>
        </main>
        <AppFooter />
      </div>
    );
  }

  return <>{children}</>;
}